import { assertUtil } from '@pukka/common/utils';
import { useSnapshot } from '@pukka/valtio';
import { twMerge } from 'tailwind-merge';

import { InformationBox } from '../../InformationBox';

import { ImageUploadFactoryOption, ImageUploadFileType, useImageUploadState } from './ImageUpload.state';
import { ImageUploadInput, ImageUploadInputPreview } from './Input';

interface ImageUploadProps<T> {
  readonly deletable?: boolean;
  readonly ordering?: boolean;
  readonly rule?: ImageUploadFactoryOption<T>['rule'];
  readonly images?: ImageUploadFactoryOption<T>['values'];
  readonly onUpdate?: ImageUploadFactoryOption<T>['onUpdate'];
  readonly removeHandler?: ImageUploadFactoryOption<T>['removeHandler'];
  readonly uploadHandler?: ImageUploadFactoryOption<T>['uploadHandler'];
  readonly className?: string;
  readonly isEmptyError?: boolean;
  readonly testId?: string;
}

function ImageUpload<T>({
  images,
  deletable = true,
  ordering = true,
  rule,
  isEmptyError,
  className,
  onUpdate,
  removeHandler,
  uploadHandler,
  testId,
}: ImageUploadProps<T>) {
  const State = useImageUploadState({
    values: images,
    deletable,
    ordering,
    rule,
    onUpdate,
    removeHandler,
    uploadHandler,
  });

  const { onAdd, onRemove, onRetryUpload, onReorder, files } = State;
  const { count, isLimitReached, order } = useSnapshot(State);

  State.useUpdate(images, deletable, ordering);

  const imageList = [...order];

  const isAllError = imageList.length > 0 && imageList.every((nonce) => files.get(nonce)?.status === 'error');

  return (
    <div className={twMerge('w-96', className)}>
      {imageList.map((nonce) => {
        const image = files.get(nonce);

        assertUtil.assertIsNotUndefined(image);

        return image.status === 'error' ? (
          <InformationBox key={nonce} variant="danger" onClick={() => onRemove(nonce)} className="mb-2">
            {image.error}
          </InformationBox>
        ) : null;
      })}
      <div className="grid min-h-[11rem] w-full grid-cols-[repeat(auto-fill,90px)] grid-rows-[repeat(auto-fill,90px)] gap-2">
        {imageList.map((nonce, i) => {
          const image = files.get(nonce);
          const isLarge = i === 0;

          assertUtil.assertIsNotUndefined(image);

          return image?.status !== 'error' ? (
            <ImageUploadInputPreview
              key={`${nonce}|${image.status}`}
              data={image as Readonly<ImageUploadFileType<T>>}
              large={isLarge}
              ordering={ordering}
              deletable={deletable}
              onRemove={onRemove}
              onRetryUpload={onRetryUpload}
              onReorder={onReorder}
              className={twMerge(isLarge ? 'col-span-2 row-span-2' : '', 'h-full w-full')}
            />
          ) : null;
        })}
        {/*  */}
        {!isLimitReached ? (
          <ImageUploadInput
            key="uploader"
            size={isAllError || count === 0 ? 's' : 'none'}
            onAdd={onAdd}
            className={twMerge(
              isAllError || count === 0 ? 'col-span-full row-span-full' : '',
              'h-full w-full',
              isEmptyError && 'border-red-500',
            )}
            multiple={rule?.maxCount ? rule.maxCount > 1 : true}
            testId={testId}
          />
        ) : null}
      </div>
    </div>
  );
}

export { ImageUpload, type ImageUploadProps };
